import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Mail, X } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/components/ui/button";
import { StatusText } from "@/shared/components/ui/status-chip";
import { useLead, useSetLeadStatus } from "../use-leads";
import { useEnrichLead } from "../use-enrichment";
import { Section, Field, CompanyBlock, RawBlock } from "./detail-parts";
import { statusTone } from "./lead-status";
import type { EnrichLeadResult, LeadDetail } from "../types";

/**
 * Deep-linkable lead detail (ADR-0020). Rendered whenever the `?lead=<id>` param
 * is present; fetches the full record and shows everything we collected —
 * identity, contacts, campaign links, screening, and the raw provenance in a
 * collapsible block. Approve/reject inline. Closing clears the URL param.
 */
export function LeadDetailDrawer({ id, onClose }: { id: string | null; onClose: () => void }) {
  const { data, isLoading, isError } = useLead(id);
  const set = useSetLeadStatus();
  const enrich = useEnrichLead();

  // Close on Escape for keyboard users.
  useEffect(() => {
    if (!id) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [id, onClose]);

  // The drawer is mounted persistently by its parents (no remount per lead), so the
  // enrich mutation's data/error would otherwise leak across leads — clear it when the
  // open lead changes so one lead's "Found …"/error line never shows under another.
  const resetEnrich = enrich.reset;
  useEffect(() => {
    resetEnrich();
  }, [id, resetEnrich]);

  if (!id) return null;

  return (
    <div className="fixed inset-0 z-50" data-testid="lead-drawer" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} data-testid="drawer-backdrop" />
      <aside className="absolute right-0 top-0 flex h-full w-full max-w-[520px] flex-col overflow-y-auto border-l border-ge-border-card bg-ge-app shadow-2xl">
        <header className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-ge-border-card bg-ge-app px-5 py-4">
          <div className="min-w-0">
            <p className="ge-eyebrow">LEAD</p>
            <h2 className="truncate text-[17px] font-semibold text-ge-primary">
              {isLoading ? "Loading…" : (data?.name ?? "Not found")}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="drawer-close"
            aria-label="Close"
            className="rounded-ge-control p-1 text-ge-muted hover:text-ge-value"
          >
            <X className="h-[18px] w-[18px]" />
          </button>
        </header>

        <div className="flex-1 px-5 py-4">
          {isError ? (
            <p className="text-[13px] text-ge-danger">Couldn't load this lead.</p>
          ) : isLoading || !data ? (
            <div className="space-y-3" aria-hidden>
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-[14px] w-full animate-pulse rounded bg-ge-border-card" />
              ))}
            </div>
          ) : (
            <LeadBody
              lead={data}
              onApprove={() => set.mutate({ id, action: "approve" })}
              onReject={() => set.mutate({ id, action: "reject" })}
              busy={set.isPending}
              onEnrich={() => enrich.mutate(id)}
              enriching={enrich.isPending}
              enrichResult={enrich.isError ? "error" : enrich.data}
            />
          )}
        </div>
      </aside>
    </div>
  );
}

function LeadBody({
  lead,
  onApprove,
  onReject,
  busy,
  onEnrich,
  enriching,
  enrichResult,
}: {
  lead: LeadDetail;
  onApprove: () => void;
  onReject: () => void;
  busy: boolean;
  onEnrich: () => void;
  enriching: boolean;
  /** Last run's result, "error" on failure, or undefined before the first run. */
  enrichResult: EnrichLeadResult | "error" | undefined;
}) {
  const hasContacts = lead.contacts.length > 0;
  return (
    <div className="space-y-5">
      {/* Deep-link to the full lead profile (this drawer is the quick peek). */}
      <Link
        to={`/leads/${lead.id}`}
        data-testid="drawer-open-profile"
        className="inline-flex items-center gap-1 text-[12px] font-medium text-ge-accent hover:underline"
      >
        Open full profile
        <ArrowUpRight className="h-[13px] w-[13px]" strokeWidth={2.2} />
      </Link>

      {/* Status + actions */}
      <div className="flex items-center justify-between">
        <StatusText tone={statusTone(lead.status)}>{lead.status}</StatusText>
        <div className="flex gap-2">
          <Button
            variant="accent-outline"
            size="sm"
            disabled={busy || enriching || lead.status === "APPROVED"}
            onClick={onApprove}
            data-testid="drawer-approve"
          >
            {lead.status === "APPROVED" ? "Approved" : "Approve"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={busy || enriching || lead.status === "REJECTED"}
            onClick={onReject}
            data-testid="drawer-reject"
          >
            {lead.status === "REJECTED" ? "Rejected" : "Reject"}
          </Button>
        </div>
      </div>

      {/* Identity */}
      <Section title="Identity">
        <Field label="Website" value={lead.website} link />
        <Field label="Phone" value={lead.phone} />
        <Field label="Address" value={lead.address} />
        <Field label="City" value={lead.city?.name ?? null} />
        <Field label="Rating" value={lead.rating != null ? lead.rating.toFixed(1) : null} />
        <Field label="Source" value={lead.source} />
        <Field label="Confidence" value={lead.confidence != null ? `${Math.round(lead.confidence * 100)}%` : null} />
        <Field label="Enriched" value={lead.enrichedAt ? new Date(lead.enrichedAt).toLocaleString() : "not yet"} />
      </Section>

      {/* Screening */}
      <Section title="Screening">
        <Field label="Dedup key" value={lead.dedupKey} mono />
        <Field label="Reason" value={lead.screenReason} />
        <Field label="Discovery run" value={lead.discoveryRunId} mono />
      </Section>

      {/* Contacts */}
      <Section
        title={`Contacts (${lead.contacts.length})`}
        action={
          <EnrichButton
            approved={lead.status === "APPROVED"}
            hasContacts={hasContacts}
            enriching={enriching}
            busy={busy}
            onEnrich={onEnrich}
          />
        }
      >
        <EnrichResultLine result={enrichResult} enriching={enriching} />
        {lead.contacts.length === 0 ? (
          <p className="text-[12px] text-ge-muted">No contacts found yet. Run enrichment to find emails.</p>
        ) : (
          <ul className="space-y-2" data-testid="drawer-contacts">
            {lead.contacts.map((c) => (
              <li key={c.id} className="rounded-ge-param border border-ge-border-card px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate font-mono text-[12px] text-ge-primary">{c.email ?? "—"}</span>
                  <span className="flex items-center gap-1 font-mono text-[10px]">
                    {c.verified && <span className="text-ge-accent">✓ verified</span>}
                    {c.optedOut && <span className="text-ge-danger">opted out</span>}
                  </span>
                </div>
                {(c.role || c.source) && (
                  <p className="mt-1 font-mono text-[10.5px] text-ge-muted">
                    {[c.role, c.source].filter(Boolean).join(" · ")}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* Campaign links */}
      {lead.campaignLeads.length > 0 && (
        <Section title="Campaigns">
          <ul className="space-y-1" data-testid="drawer-campaigns">
            {lead.campaignLeads.map((cl) => (
              <li key={cl.id} className="flex items-center justify-between font-mono text-[12px]">
                <span className="truncate text-ge-value">{cl.campaign?.name ?? cl.campaignId}</span>
                {cl.campaign?.status && <span className="text-ge-faint">{cl.campaign.status}</span>}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Company firmographics captured during enrichment (ADR-0024) */}
      <CompanyBlock aiData={lead.aiData} />

      {/* Raw provenance */}
      <RawBlock title="Raw provenance" value={lead.provenance} />
      <RawBlock title="AI data" value={lead.aiData} />
    </div>
  );
}

/**
 * On-demand enrichment for this lead (ADR-0024). Only APPROVED leads are eligible —
 * mirrors the bulk run and the server guard — so for anything else the button is disabled
 * with a hint. Re-running is intentional: it refreshes contacts + firmographics.
 */
function EnrichButton({
  approved,
  hasContacts,
  enriching,
  busy,
  onEnrich,
}: {
  approved: boolean;
  hasContacts: boolean;
  enriching: boolean;
  busy: boolean;
  onEnrich: () => void;
}) {
  const label = enriching ? "Enriching…" : hasContacts ? "Re-enrich" : "Find emails";
  // aria-label carries the reason even while disabled (title tooltips don't fire on
  // disabled buttons in most browsers, so a screen reader would otherwise get nothing).
  const hint = approved ? "Search Hunter for contacts and company data" : "Approve the lead first to enrich it";
  return (
    <Button
      variant="accent-outline"
      size="sm"
      disabled={enriching || busy || !approved}
      onClick={onEnrich}
      data-testid="drawer-enrich"
      title={hint}
      aria-label={`${label} — ${hint}`}
    >
      <Mail className="h-[13px] w-[13px]" strokeWidth={2.2} />
      {label}
    </Button>
  );
}

/** Transient result line under the Contacts header after a manual enrich run. */
function EnrichResultLine({
  result,
  enriching,
}: {
  result: EnrichLeadResult | "error" | undefined;
  enriching: boolean;
}) {
  if (enriching || result === undefined) return null;
  let text: string;
  let tone: "accent" | "danger" | "muted" = "muted";
  if (result === "error") {
    text = "Enrichment failed — try again.";
    tone = "danger";
  } else if (result.email) {
    text = `Found ${result.email}${result.verified ? " (verified)" : ""}.`;
    tone = "accent";
  } else if (result.skippedNoDomain) {
    text = "No website on this lead for Hunter to search.";
  } else {
    text = "No email found for this lead.";
  }
  return (
    <p
      data-testid="drawer-enrich-result"
      className={cn(
        "text-[11.5px]",
        tone === "accent" && "text-ge-accent",
        tone === "danger" && "text-ge-danger",
        tone === "muted" && "text-ge-muted",
      )}
    >
      {text}
    </p>
  );
}

